import * as React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Typography, Accordion, AccordionSummary, AccordionDetails, Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ReactComponent as DocumentIcon } from '@/assets/icons/documentIcon.svg';
import { DynamicComponentProps } from '@/types/ModuleComponentProp.type';
import { getProductDisplayText } from '@/utils/inspection.utils';
import { RootState } from '@/store/rootReducer';
import { APP_CONFIG_KEY } from '@/types/AppConfig.type';
import styles from './InspectionSummary.module.scss';

interface SummaryRowProps {
    label: string;
    value: string | number | null | undefined;
    isFirst?: boolean;
    isLast?: boolean;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ label, value, isFirst = false, isLast = false }) => {
    const { t: translate } = useTranslation();
    
    return (
        <Grid 
            container 
            justifyContent="space-between" 
            alignItems="center" 
            sx={{ 
                borderTop: isFirst ? ".05rem solid #A7A7A7" : "none",
                borderBottom: isLast ? "none" : ".05rem solid #ECECEB", 
                padding: '.67rem 0' 
            }}
        >
            <Grid size={{ xs: 1, sm: 2 }}></Grid>
            <Grid size={3}>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: "600" }}>
                    {translate(label)}:
                </Typography>
            </Grid>
            <Grid size={{ xs: 8, sm: 7 }}>
                <Typography variant="body1">{value || '-'}</Typography>
            </Grid>
        </Grid>
    );
};

export const InspectionSummary: React.FC<DynamicComponentProps> = (props: DynamicComponentProps) => {
    const { inspection, structure } = props;
    const { t: translate } = useTranslation();
    const config = useSelector((state: RootState) => state.appConfig);

    const initialExpanded = sessionStorage.getItem('inspectionSummaryExpanded') === 'true';
    const [expanded, setExpanded] = React.useState<boolean>(initialExpanded);

    React.useEffect(() => {
        const savedState = sessionStorage.getItem('inspectionSummaryExpanded');
        if (savedState !== null) {
            setExpanded(savedState === 'true');
        }
    }, []);

    const handleAccordionChange = (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded);
        sessionStorage.setItem('inspectionSummaryExpanded', String(isExpanded));
    };

    if (!inspection) return null;

    const showExternalPOFilter = config.filters?.some(filter => filter === 'externalPO');
    const showInternalRef = config[APP_CONFIG_KEY.showInternalOrderRef] || false;

    const summaryData = [
        { label: (config?.[APP_CONFIG_KEY.useText] as string) ?? 'Product', value: getProductDisplayText(inspection.product) },
        { label: 'Supplier', value: inspection.supplier?.name ?? 'Unknown' },
        ...(showExternalPOFilter 
            ? [{ label: 'External Order Ref.', value: inspection.externalOrderReference }]
            : [{ label: 'Order', value: inspection.internalOrderReference }]
        ),
        ...(showInternalRef 
            ? [{ label: 'Internal Order Ref.', value: inspection.internalOrderReference }]
            : [{ label: 'Colour', value: inspection.colours || inspection.colourCodes }]
        ),
        { label: 'Lot Size', value: inspection.lotSize },
        { label: 'Sample Size', value: inspection.sampleSize },
        { label: 'Inspection Type', value: inspection.controlPoint?.name },
        { label: 'Inspection Method', value: inspection.inspectionLevel?.name }
    ];

    return (
        <div className={styles.container}>
            <Accordion
                expanded={expanded}
                onChange={handleAccordionChange}
                sx={{
                    width: '100%',
                    boxShadow: 'none',
                    padding: '.34rem 0 1rem 0'
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel3-content"
                    id="panel3-header"
                    sx={{
                        flexDirection: 'column',
                        '& .MuiAccordionSummary-content': {
                            marginBottom: 0,
                            width: '100%'
                        },
                        '& .MuiAccordionSummary-expandIconWrapper': {
                            position: 'absolute',
                            right: 16,
                            top: 'calc(50% - 12px)',
                        }
                    }}
                >
                    <h2 className={styles.header}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingBottom: expanded ? '0.5rem' : '0'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '0 1.67rem 0 .67rem',
                                borderRight: '.11rem solid #e0e0e0',
                            }}
                        >
                            <DocumentIcon style={{ width: '40px', height: '40px' }} />
                        </Box>
                        <span style={{ flex: 1, textAlign: 'center', paddingRight: '60px' }}>{structure.header && translate(structure.header)}</span>
                    </h2>
                </AccordionSummary>
                <AccordionDetails className={styles.details}>
                    <Grid direction='column'>
                        {summaryData.map((item, index) => (
                            <SummaryRow
                                key={item.label}
                                label={item.label}
                                value={item.value}
                                isFirst={index === 0}
                                isLast={index === summaryData.length - 1}
                            />
                        ))}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </div>
    );
}